import * as React from 'react'
import {
  type Block,
  getBlockUpdateOverride,
  getValueBasedOnSelectedVariants,
  MediaBlock,
  MediaBlockValueType,
  mediaBlockValueTypeSchema,
} from '@basehub/data/replicache/index'

import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { getErrorMessage } from '../../../../../../../../../../lib/get-error-message'
import * as PropertiesPanel from '../../properties-panel'
import { useSuggestedAltText } from './use-suggested-alt-text'
import {
  FileIcon,
  ImageIcon,
  SpeakerLoudIcon,
  SymbolIcon,
  TrashIcon,
  VideoIcon,
} from '@radix-ui/react-icons'
import { Button } from '../../../../../../../../../../sotano-kit/primitives/button'
import { widthAndHeightToAspectRatio } from '@basehub/utils/math/index'
import { formatBytes } from '../../../../../../../../../../lib/file-utils'
import { fromUnixTime } from 'date-fns'
import { AIIcon } from '../../../../../../../../../../components/icons/ai'
import {
  SUGGESTED_ALT_TEXT_COMPATIBLE_MIME_TYPES,
  getAllowedMimeTypesArray,
  getDefaultMimeTypesByCategory,
} from '@basehub/data/replicache/schema/block/valid-mime-types'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import {
  productionAssetsURL,
  productionR2Origin,
} from '@basehub/shared-constants'
import { useEditorStore } from '../../editor-store'
import { useSelectedVariants } from '~/hooks/use-selected-variants'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { useTabsStore } from '../../tabs/store'
import { useEditorMode } from '../../editor-mode-provider'
import { arraysHaveSameItems, prettifyMimeType } from './utils'
import { toast } from 'sonner'
import { MaxSizeUnit } from '@basehub/utils/image-utils'

const FORMAT_SETS: Record<string, PropertiesPanel.CheckListOption> = {
  image: {
    label: 'Image',
    icon: <ImageIcon />,
    value: 'image',
  },
  video: {
    label: 'Video',
    icon: <VideoIcon />,
    value: 'video',
  },
  audio: {
    label: 'Audio',
    icon: <SpeakerLoudIcon />,
    value: 'audio',
  },
  file: {
    label: 'File',
    icon: <FileIcon />,
    value: 'file',
  },
}

export const MediaBlockPropertiesPanel = ({
  block,
  parent,
  path,
  focusableElementSelector,
}: {
  block: MediaBlock
  parent: Block
  path: string
  focusableElementSelector: string
}) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const undoManager = useRepoUndoManager()
  const editorMode = useEditorMode()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const sourceBlockId = idToSourceBlockId(block)
  const getInputFocusOp = React.useCallback(
    (id: string | undefined) => {
      return getBlockFocusOp({
        block: {
          id: block.id,
          sourceBlockId: block.sourceBlockId,
        },
        preferredTabKey: activeTabKey,
        focusType: 'block:properties-panel',
        elementIdOverride: id,
      })
    },
    [block.id, block.sourceBlockId, activeTabKey]
  )

  const isSelected = useEditorStore(
    (s) => s.selectedBlockId === idToSourceBlockId(block)
  )
  const selectedDashboardVariants = useSelectedVariants({
    parentBlock: parent,
    block,
  })

  const value = React.useMemo(
    () =>
      getValueBasedOnSelectedVariants({
        block,
        variantsToFilterBy: selectedDashboardVariants,
      }),
    [block, selectedDashboardVariants]
  )

  const media = value.status === 'uploaded' ? (value ?? null) : null

  const typeCompatibleWithAltText =
    SUGGESTED_ALT_TEXT_COMPATIBLE_MIME_TYPES.includes(media?.mimeType ?? '')

  const shouldSuggestAltText =
    isSelected &&
    Boolean(media && 'altText' in media && media.altText) === false &&
    typeCompatibleWithAltText

  const {
    data: suggestedAltText,
    isFetching,
    error,
  } = useSuggestedAltText(shouldSuggestAltText && media ? media.url : null)

  const getInputElement = React.useCallback(() => {
    const blockWrapper = document.querySelector<HTMLDivElement>(
      `[data-block-id="${idToSourceBlockId(block)}"]`
    )
    if (!blockWrapper) return
    const input = blockWrapper.querySelector<HTMLInputElement>(
      focusableElementSelector
    )
    if (input) return input
  }, [block, focusableElementSelector])

  React.useEffect(() => {
    if (!isSelected) return

    const handlePaste = (e: ClipboardEvent) => {
      const activeElement = document.activeElement
      if (
        activeElement !== getInputElement() &&
        activeElement !== document.body
      ) {
        return
      }
      e.preventDefault()
      e.stopPropagation()
      const files = e.clipboardData?.files
      const file = files?.[0]
      if (file) {
        // (try to) upload it!
        const input = getInputElement()
        if (input) {
          if (input.disabled) return

          if (
            !getAllowedMimeTypesArray(
              block.constraints.allowedMimeTypes
            ).includes(file.type)
          ) {
            return toast.error(
              `File in clipboard is not allowed in '${
                block.title || 'Untitled'
              }'.`,
              {
                action: {
                  label: `Add '${file.type}' to allowed formats.`,
                  onClick: async () => {
                    if (!replicache) return
                    let category: MediaBlockValueType
                    // exceptions
                    if (file.type.includes('adobe.photoshop')) {
                      category = 'file'
                    } else {
                      category = mediaBlockValueTypeSchema
                        .catch('file')
                        .parse(file.type.split('/')[0])
                    }

                    let previous =
                      block.constraints.allowedMimeTypes?.[category]
                    if (previous === true) {
                      previous = getDefaultMimeTypesByCategory(category)
                    } else if (!Array.isArray(previous)) {
                      previous = []
                    }

                    await replicache.mutate.updateBlock({
                      id: block.id,
                      strategy: 'merge-deep',
                      path,
                      instanceChildTarget: block.instanceChildTarget,
                      isoNow: new Date().toISOString(),
                      data: {
                        constraints: {
                          allowedMimeTypes: {
                            [category]: [...previous, file.type],
                          },
                        },
                      },
                    })

                    input.files = files
                    const event = new Event('change', { bubbles: true })
                    input.dispatchEvent(event)
                  },
                },
              }
            )
          }
          // trigger change event
          input.files = files
          const event = new Event('change', { bubbles: true })
          input.dispatchEvent(event)
        }
      }
    }

    document.addEventListener('paste', handlePaste)
    return () => {
      document.removeEventListener('paste', handlePaste)
    }
  }, [
    block.constraints.allowedMimeTypes,
    block.id,
    block.instanceChildTarget,
    block.title,
    getInputElement,
    isSelected,
    path,
    replicache,
  ])

  const handleUpdateAltText = React.useCallback(
    async (v: string) => {
      const params: UpdateBlockParams = {
        id: block.id,
        instanceChildTarget: block.instanceChildTarget,
        data: getBlockUpdateOverride({
          newValue: { altText: v || null },
          block,
          selectedVariants: selectedDashboardVariants,
        }),
        path,
        strategy: 'merge-deep',
        isoNow: new Date().toISOString(),
      }
      const result = await replicache?.mutate.updateBlock(params)
      if (result?.undo) {
        undoManager.current?.push({
          redo: [
            getInputFocusOp(`el-${sourceBlockId}-pp-alt-text`),
            { op: 'updateBlock', args: params },
            {
              op: 'customWindowEvent',
              args: {
                name: `${sourceBlockId}-pp-alt-text`,
                detail: { action: 'sync' },
              },
            },
          ],
          undo: [
            getInputFocusOp(`el-${sourceBlockId}-pp-alt-text`),
            {
              op: 'blockBatchMutations',
              args: { mutations: result.undo },
            },
            {
              op: 'customWindowEvent',
              args: {
                name: `${sourceBlockId}-pp-alt-text`,
                detail: { action: 'sync' },
              },
            },
          ],
        })
      }
    },
    [
      block,
      getInputFocusOp,
      path,
      replicache?.mutate,
      selectedDashboardVariants,
      sourceBlockId,
      undoManager,
    ]
  )

  const [availableFormatSets, formatSetsInUse] = React.useMemo(() => {
    if (!block.constraints.allowedMimeTypes) {
      return [Object.values(FORMAT_SETS), []]
    }
    const available: PropertiesPanel.CheckListOption[] = []
    const inUse: PropertiesPanel.CheckListOption[] = []
    Object.values(FORMAT_SETS).forEach((set) => {
      const match =
        block.constraints.allowedMimeTypes?.[set.value as MediaBlockValueType]
      if (!match) {
        available.push(set)
      } else {
        inUse.push({
          ...set,

          description:
            match === true
              ? 'All formats.'
              : !match.length
                ? 'No formats selected.'
                : match.map((f) => prettifyMimeType(f)).join(', '),
        })
      }
    })
    return [available, inUse]
  }, [block.constraints.allowedMimeTypes])

  const handleFormatSetUpdate = React.useCallback(
    async (category: string, enabled: boolean) => {
      if (!replicache) return
      const params: UpdateBlockParams = {
        id: block.id,
        instanceChildTarget: block.instanceChildTarget,
        data: {
          type: block.type,
          constraints: {
            allowedMimeTypes: {
              ...block.constraints.allowedMimeTypes,
              [category]: enabled ? true : null,
            },
          },
        },
        path,
        strategy: 'merge-deep',
        isoNow: new Date().toISOString(),
      }

      const { undo } = await replicache.mutate.updateBlock(params)
      if (undo) {
        undoManager.current?.push({
          redo: [
            getInputFocusOp(`el-${sourceBlockId}-pp-formats`),
            { op: 'updateBlock', args: params },
          ],
          undo: [
            getInputFocusOp(`el-${sourceBlockId}-pp-formats`),
            {
              op: 'blockBatchMutations',
              args: { mutations: undo },
            },
          ],
        })
      }
    },
    [
      block.constraints.allowedMimeTypes,
      block.id,
      block.instanceChildTarget,
      block.type,
      getInputFocusOp,
      path,
      replicache,
      sourceBlockId,
      undoManager,
    ]
  )

  const getAllowedFormatsByCategory = React.useCallback(
    (category: string): PropertiesPanel.CheckItem[] => {
      let formats =
        block.constraints.allowedMimeTypes?.[category as MediaBlockValueType]
      if (!formats) return []
      if (formats === true) {
        formats = getDefaultMimeTypesByCategory(category)
      }
      return formats.map((f) => ({ name: prettifyMimeType(f), id: f }))
    },
    [block.constraints.allowedMimeTypes]
  )

  const handleFormatUpdate = React.useCallback(
    async ({
      newValue,
      items,
      setId,
    }: {
      newValue: boolean | 'overwrite'
      items: PropertiesPanel.CheckItem[]
      setId: string
    }) => {
      let update
      let previousValue =
        block.constraints.allowedMimeTypes?.[setId as MediaBlockValueType] || []
      if (previousValue === true) {
        previousValue = getDefaultMimeTypesByCategory(setId)
      }
      if (newValue === 'overwrite') {
        update = items.map((item) => item.id)
      } else if (newValue) {
        update = [...previousValue, ...items.map((item) => item.id)]
      } else {
        update =
          previousValue?.filter((item) => !items.some((i) => i.id === item)) ??
          false
      }

      if (arraysHaveSameItems(update, getDefaultMimeTypesByCategory(setId))) {
        update = true
      }

      const params: UpdateBlockParams = {
        id: block.id,
        instanceChildTarget: block.instanceChildTarget,
        data: {
          type: block.type,
          constraints: {
            allowedMimeTypes: {
              ...block.constraints.allowedMimeTypes,
              [setId]: update,
            },
          },
        },
        path,
        strategy: 'merge-deep',
        isoNow: new Date().toISOString(),
      }

      const result = await replicache?.mutate.updateBlock(params)
      if (result?.undo) {
        undoManager.current?.push({
          redo: [
            getInputFocusOp(`el-${sourceBlockId}-pp-formats`),
            { op: 'updateBlock', args: params },
          ],
          undo: [
            getInputFocusOp(`el-${sourceBlockId}-pp-formats`),
            {
              op: 'blockBatchMutations',
              args: { mutations: result.undo },
            },
          ],
        })
      }
    },
    [
      block.constraints.allowedMimeTypes,
      block.id,
      block.instanceChildTarget,
      block.type,
      getInputFocusOp,
      path,
      replicache?.mutate,
      sourceBlockId,
      undoManager,
    ]
  )

  return (
    <>
      <PropertiesPanel.PropertiesContainer>
        <PropertiesPanel.BlockTitleGroup
          block={block}
          path={path}
        ></PropertiesPanel.BlockTitleGroup>

        {/* FILE INFO PROPERTIES */}
        {value.status === 'uploaded' && (
          <PropertiesPanel.CollapsibleGroup
            id="asset-details"
            title="file details"
          >
            {value.status === 'uploaded' && value.type === 'image' && (
              <PropertiesPanel.AlwaysVisibleContent className="!mb-2">
                <PropertiesPanel.TextOption
                  multiLine
                  maxLength={320}
                  id={`el-${sourceBlockId}-pp-alt-text`}
                  name="Alt text"
                  value={value.altText ?? ''}
                  forceEditable
                  mutate={handleUpdateAltText}
                  tooltipText="Explain to screen readers and search engines what the image is about"
                >
                  {(isFetching || suggestedAltText) && (
                    <div className="absolute left-0 bottom-0 p-2 pb-1 w-full">
                      <button
                        type="button"
                        className="font-medium flex gap-1.5 items-center overflow-hidden text-ellipsis whitespace-nowrap w-full text-left text-[11px] text-dark-muted hover:text-dark-dim focus:text-dark-dim focus:underline cursor-default focus:outline-none"
                        onClick={() => {
                          suggestedAltText &&
                            handleUpdateAltText(suggestedAltText)
                        }}
                        disabled={!suggestedAltText}
                        title={suggestedAltText ?? undefined}
                      >
                        <span className="shrink-0">
                          <AIIcon style={{ color: 'inherit' }} />
                        </span>
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap w-full">
                          {suggestedAltText ?? (
                            <>&nbsp;Generating relevant alt text...</>
                          )}
                        </span>
                      </button>
                    </div>
                  )}
                </PropertiesPanel.TextOption>
                {!!error && (
                  <p className="text-xs text-dark-dim">
                    {getErrorMessage(error)}
                  </p>
                )}
              </PropertiesPanel.AlwaysVisibleContent>
            )}

            <PropertiesPanel.CollapsibleContent>
              <PropertiesPanel.Property name="Name:" value={value.fileName} />
              <PropertiesPanel.Property
                name="Size:"
                value={formatBytes(value.fileSize)}
              />
              <PropertiesPanel.Property
                name="MIME type:"
                value={value.mimeType}
              />
              {value.type === 'image' && (
                <>
                  <PropertiesPanel.Property
                    name="Width:"
                    value={value.width + 'px'}
                  />
                  <PropertiesPanel.Property
                    name="Height:"
                    value={value.height + 'px'}
                  />
                  <PropertiesPanel.Property
                    name="Aspect ratio:"
                    value={widthAndHeightToAspectRatio(
                      value.width,
                      value.height
                    )}
                  />
                </>
              )}
              <PropertiesPanel.Property
                name="Last modified:"
                value={fromUnixTime(
                  Math.floor(value.lastModified / 1000)
                ).toDateString()}
              />
              <PropertiesPanel.Property
                name="URL:"
                value={value.url.replace(
                  productionR2Origin,
                  productionAssetsURL
                )}
              />
            </PropertiesPanel.CollapsibleContent>
          </PropertiesPanel.CollapsibleGroup>
        )}

        <PropertiesPanel.Group title="Constraints" spacing="tight">
          <PropertiesPanel.BooleanOption
            name="Is required"
            id={`el-${sourceBlockId}-pp-is-required`}
            value={block.constraints.isRequired}
            mutate={async (v) => {
              const params: UpdateBlockParams = {
                id: block.id,
                instanceChildTarget: block.instanceChildTarget,
                data: {
                  type: block.type,
                  constraints: { isRequired: v },
                },
                path,
                strategy: 'merge-deep',
                isoNow: new Date().toISOString(),
              }
              const result = await replicache?.mutate.updateBlock(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-is-required`),
                    { op: 'updateBlock', args: params },
                  ],
                  undo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-is-required`),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                  ],
                })
              }
            }}
          />
          <PropertiesPanel.MaxSizeOption
            id={`el-${sourceBlockId}-pp-max-size`}
            size={{
              value: block.constraints.maxSize?.split('-')[0],
              unit:
                (block.constraints.maxSize?.split('-')[1] as MaxSizeUnit) ||
                'mb',
            }}
            mutate={async (v, u) => {
              const params: UpdateBlockParams = {
                instanceChildTarget: block.instanceChildTarget,
                id: block.id,
                data: {
                  type: block.type,
                  constraints: {
                    maxSize: v !== '' ? `${v}-${u}` : null,
                  },
                },
                path,
                strategy: 'merge-deep',
                isoNow: new Date().toISOString(),
              }
              const result = await replicache?.mutate.updateBlock(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-max-size`),
                    { op: 'updateBlock', args: params },
                    {
                      op: 'customWindowEvent',
                      args: {
                        name: `${sourceBlockId}-pp-max-size`,
                        detail: { action: 'sync' },
                      },
                    },
                  ],
                  undo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-max-size`),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                    {
                      op: 'customWindowEvent',
                      args: {
                        name: `${sourceBlockId}-pp-max-size`,
                        detail: { action: 'sync' },
                      },
                    },
                  ],
                })
              }
            }}
          />
        </PropertiesPanel.Group>
        <PropertiesPanel.MultiCheckListsOption
          title="Formats"
          emptyState="Allowed formats will show up here."
          handleItemsToggle={handleFormatUpdate}
          getItemsByCheckList={(set) =>
            getDefaultMimeTypesByCategory(set).map((f) => ({
              name: prettifyMimeType(f),
              id: f,
            }))
          }
          checkListPopoverProps={{
            filterById: true,
          }}
          availableOptionSets={availableFormatSets}
          setsInUse={formatSetsInUse}
          getCheckedChildren={getAllowedFormatsByCategory}
          setOption={handleFormatSetUpdate}
          removeLabel="Remove format category"
          iconButtonProps={{ id: `el-${sourceBlockId}-pp-formats` }}
        />

        {editorMode !== 'read' && value.status !== 'idle' && (
          <>
            <PropertiesPanel.Group
              noBorderTop
              className="fixed bottom-0 bg-gradient-to-b from-transparent to-dark-bg pt-8"
              style={{ width: 'calc(var(--resizer-width) - 40px)' }}
            >
              <div className="flex gap-2">
                <Button
                  intent="secondary"
                  className="grow text-xs"
                  type="button"
                  onClick={() => {
                    const input = getInputElement()
                    if (input) input.click()
                  }}
                >
                  <SymbolIcon width={16} height={16} className="mr-1" />
                  Replace
                </Button>
                <Button
                  type="button"
                  id={`el-${sourceBlockId}-pp-remove-media`}
                  intent="secondary"
                  className="grow text-xs text-dark-error"
                  onClick={async () => {
                    const params: UpdateBlockParams = {
                      id: block.id,
                      instanceChildTarget: block.instanceChildTarget,
                      data: getBlockUpdateOverride({
                        newValue: { status: 'idle' },
                        block,
                        selectedVariants: selectedDashboardVariants,
                      }),
                      path,
                      strategy: 'merge',
                      isoNow: new Date().toISOString(),
                    }
                    const result = await replicache?.mutate.updateBlock(params)
                    if (result?.undo) {
                      undoManager.current?.push({
                        redo: [
                          getInputFocusOp(
                            `el-${sourceBlockId}-pp-remove-media`
                          ),
                          { op: 'updateBlock', args: params },
                        ],
                        undo: [
                          getInputFocusOp(undefined),
                          {
                            op: 'blockBatchMutations',
                            args: { mutations: result.undo },
                          },
                        ],
                      })
                    }
                  }}
                >
                  <TrashIcon width={16} height={16} className="mr-1" />
                  Remove
                </Button>
              </div>
            </PropertiesPanel.Group>
          </>
        )}
      </PropertiesPanel.PropertiesContainer>
    </>
  )
}
